import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import RadioCircleIcon from '@/CustomIcons/RadioCircleIcon'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { IconCircle } from '@/components/CircleIcon'
import { cn } from '@/utlis/cn'
import DiagonalTabs from '@/components/DiagonalTabs'
import { useIsProUser } from '@/services/useIsProUser'
import { DashboardRoom, ExternalEvent, useAppContext } from '@/AppContext'
import { useUserContext } from '@/UserContext'
import { SessionFeedbackPopup, SessionFeedbackResult } from '@/components/SessionFeedbackPopup'
import { logger } from '@/services/Logger'
import { env } from '@/services/EnvironmentVariables'
import useRootSnackbar from '@/services/useRootSnackbar'
import { Loader } from '@/components/Loader'
import { DashRoomCard } from './internal/DashRoomCard'
import Carousel from './internal/Carousel'
import { ApiFrontPageResponse, NewsCard, useFrontpage } from './internal/useFrontpage'
import { useAppCloseHandler } from './internal/useAppCloseHandler'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import { EmptyTab } from './internal/EmptyTab'
import { ScheduledSessions } from './internal/ScheduledSessions'
import DashboardDialogs from './internal/DashboardDialogs'
import AppNewsCard from '@/pages/DashboardPage/internal/CarouselCards/AnnouncementsCard'
import StatsCard, { ApiStatsResponse } from '@/pages/DashboardPage/internal/CarouselCards/StatsCard'
import BoostsCard from '@/pages/DashboardPage/internal/CarouselCards/BoostsCard'
import { parseJoinRoomLink } from '@/services/JoinRoomLink'
import ElectronIPC from '@/services/ElectronIPC'
import { ExternalEventEmitter } from '@/ExternalEventEmitter'
import { useNonIdempotentEffect } from '@/hooks/useNonIdempotentEffect'
import { persistent } from '@/services/LocalStorage'
import * as Constants from '@/services/Constants'
import useScheduledSessionsFilter from '@/services/useScheduledSessionsFilter'
import { Trans, useTranslation } from 'react-i18next'
import { Username } from '@/components/Username'
import { useResource } from '@/services/useResource'
import CalendarAddIcon from '@/CustomIcons/CalendarAddIcon'
import { TopBar } from './internal/TopBar'
import { carmackClick } from '@/utils'
import OSXTitleBar from '@/components/OSXTitleBar'
import AccountNotVerifiedAlert from '@/components/AccountNotVerifiedAlert'

const log = logger.createChildLogger({ name: 'DashboardPage' })

export function DashboardPage() {
    const { frontpage, frontpageLoading } = useFrontpage()
    const [userState] = useUserContext()
    useAppCloseHandler()
    useBusyAppKillBlocker()
    useExternalEventHandler()
    useRestoreWindowBounds()
    const scheduledSessionsFilter = useScheduledSessionsFilter(frontpage?.scheduledSessions ?? [])
    const { showFeedback, handleFeedbackClose } = useFeedback()

    const bgStyle = 'overflow-y-hidden'
    const pagePadding = ['py-2 md:py-4', 'px-2 sm:px-20 md:px-0']

    return (
        <div className={cn('h-[100vh] min-w-full flex flex-col items-center', bgStyle)}>
            <OSXTitleBar />
            <div className={cn('flex flex-col h-dvh w-full overflow-y-hidden max-w-[1280px]', pagePadding)}>
                <TopBar className="md:pl-[5.6vw] pb-[15px] md:pb-[20px] lg:pb-[60px]" />
                <div className="h-full min-h-0 grid grid-cols-1 md:grid-cols-[1fr_min-content]">
                    <div className="min-h-0 flex flex-col min-w-0 overflow-y-auto md:px-[5.6vw]">
                        <GreetingMessage
                            unverified={!userState.profile?.emailVerified}
                            className="pb-[16px] lg:pb-[32px] pl-1"
                            scheduledSessionsCount={scheduledSessionsFilter.sessionsToday.length}
                        />
                        <FeatureButtons className="pb-[12px] lg:pb-[24px]" />
                        <DashboardNews isLoading={frontpageLoading} newsCards={frontpage?.newsCards ?? []} />
                    </div>
                    <SessionTabs
                        frontpage={frontpage}
                        loading={frontpageLoading}
                        scheduledSessionsFilter={scheduledSessionsFilter}
                    />
                </div>
            </div>
            <SessionFeedbackPopup open={showFeedback} onClose={handleFeedbackClose} sessionId="123" />
            <DashboardDialogs />
        </div>
    )
}

function FeatureButtons(props: { className?: string }) {
    const history = useHistory()
    const { t } = useTranslation()
    const [, appDispatch] = useAppContext()
    const logUserActivityWithContext = useAnalytics().logUserActivityWithContext

    const handleScheduleSession = () => {
        logUserActivityWithContext(AnalyticsEvents.CLICKED_SCHEDULE_SESSION_BUTTON)
        appDispatch({
            type: 'update_app_state',
            value: {
                showScheduleSessionDialog: { isOpen: true },
            },
        })
    }

    const containerStyles = ['max-w-full', 'grid grid-cols-2', 'lg:grid-cols-4', 'gap-[3px]']

    return (
        <div className={cn(containerStyles, props.className)}>
            <HostSessionButton className="col-span-2" />
            <DashSecondaryButton onClick={() => history.push('/join')}>
                <IconCircle className="absolute w-[50px] h-[50px] top-[13px] right-[13px] backdrop-blur-xs">
                    <RightArrowIcon style={{ fontSize: '1.4rem' }} />
                </IconCircle>
                <Trans i18nKey="<1>Join</1> <2>Session</2>" t={t}>
                    <div>Join</div>
                    <div className="font-extralight opacity-65">Session</div>
                </Trans>
            </DashSecondaryButton>
            <DashSecondaryButton onClick={() => handleScheduleSession()}>
                <IconCircle className="absolute w-[50px] h-[50px] top-[13px] right-[13px] backdrop-blur-xs">
                    <CalendarAddIcon style={{ fontSize: '1.4rem' }} />
                </IconCircle>
                <Trans i18nKey="<1>Schedule</1> <2>Session</2>" t={t}>
                    <div>Schedule</div>
                    <div className="font-extralight opacity-65">Session</div>
                </Trans>
            </DashSecondaryButton>
        </div>
    )
}

function DashboardNews({ isLoading, newsCards }: { isLoading: boolean; newsCards: Array<NewsCard> }) {
    const isProUser = useIsProUser()
    const { data: stats } = useResource<ApiStatsResponse>('/v1/users/session-stats')

    return (
        <Carousel
            className="object-cover mr-6 md:mr-0 max-w-[500px] md:max-w-full lg:max-w-[680px]"
            contents={
                isLoading
                    ? [
                          <div
                              key="loading1"
                              className="w-full h-full flex items-center justify-center bg-[rgba(21,21,21,0.85)] border-muse-toplit rounded-[26px] shadow-glow"
                          >
                              <Loader className="bg-neutral-700" width="10%" />
                          </div>,
                          <div
                              key="loading2"
                              className="w-full h-full flex items-center justify-center bg-[rgba(21,21,21,0.85)] border-muse-toplit rounded-[26px] shadow-glow"
                          >
                              <Loader className="bg-neutral-700" width="10%" />
                          </div>,
                          <div
                              key="loading3"
                              className="w-full h-full flex items-center justify-center bg-[rgba(21,21,21,0.85)] border-muse-toplit rounded-[26px] shadow-glow"
                          >
                              <Loader className="bg-neutral-700" width="10%" />
                          </div>,
                      ]
                    : [
                          ...newsCards.map((card) => <AppNewsCard key={card.id} card={card} />),
                          !isProUser ? <BoostsCard /> : null,
                          <StatsCard stats={stats} />,
                          // <TutorialsCard />,
                      ]
            }
        />
    )
}

function useFeedback(): { showFeedback: boolean; handleFeedbackClose: (feedback: SessionFeedbackResult) => void } {
    const history = useHistory()
    const [userState] = useUserContext()
    const snackbar = useRootSnackbar()

    const [feedbackCtx, setFeedbackCtx] = useState<{ roomUrn: string; sessionId: string } | null>(
        env.SHOW_ROOM_EXIT_FEEDBACK_ON_LOAD ? { roomUrn: '(test)', sessionId: '(test)' } : null
    )
    useEffect(() => {
        if (history.location.search) {
            const q = new URLSearchParams(history.location.search)
            const feedbackParam = q.get('feedback')
            if (feedbackParam) {
                console.log(`ChooseInitialModePage: handling feedback="${feedbackParam}".`)

                const [feedbackRoom, feedbackSession] = feedbackParam.split(':')

                setTimeout(() => {
                    setFeedbackCtx({ roomUrn: feedbackRoom, sessionId: feedbackSession })
                }, 250)
            }
        }
    }, [])
    const handleFeedbackClose = (feedback: SessionFeedbackResult) => {
        const log = logger.createChildLogger({
            name: 'RTCSessionManager',
            roomUrn: feedbackCtx?.roomUrn,
            sessionId: feedbackCtx?.sessionId,
        })

        log.checkpoint('room_exit_feedback', '', {
            roomUrn: feedbackCtx?.roomUrn,
            sessionId: feedbackCtx?.sessionId,
            userUrn: userState.userUrn,
            username: userState.profile?.username || null,
            os: env.OS,
            osVersion: env.OS_RELEASE,
            appVersion: env.APP_VERSION || '',
            ...feedback,
        })

        logger.flushLogs().then(() => {})

        if (feedback.exitMode !== 'click-x') {
            snackbar.show({
                severity: 'success',
                message: 'We appreciate your feedback!',
            })
        }

        setFeedbackCtx(null)

        // Strip the query param to avoid re-trigger of feedback modal
        const url = new URL(window.location.toString())
        url.searchParams.delete('feedback')
        window.history.replaceState({}, '', url)
    }

    return { showFeedback: feedbackCtx != null, handleFeedbackClose }
}

type ScheduledSessionsNoticeProps = {
    className?: string
    scheduledSessionsCount: number
    unverified: boolean
}
function GreetingMessage({ className, scheduledSessionsCount, unverified }: ScheduledSessionsNoticeProps) {
    const { t } = useTranslation()
    const topLineFont = 'text-[28px] font-medium tracking-wider'
    const bottomLineFont = 'text-[28px] font-extralight tracking-wider'

    if (unverified) {
        return (
            <div className={cn(className)}>
                <AccountNotVerifiedAlert active={unverified} />
            </div>
        )
    }

    if (scheduledSessionsCount === 0) {
        return (
            <div className={className}>
                <div className={topLineFont}>{t('Hello')},</div>
                <div className={bottomLineFont}>
                    <Username isMe />!
                </div>
            </div>
        )
    }

    return (
        <div className={className}>
            <Trans
                i18nKey="<0>You have</0><1>{{count}} scheduled sessions</1>"
                t={t}
                components={[<div className={topLineFont} />, <div className={bottomLineFont} />]}
                count={scheduledSessionsCount}
            />
        </div>
    )
}

function HostSessionButton({ className }: { className?: string }) {
    const { t } = useTranslation()
    const history = useHistory()

    const style =
        'relative group w-full max-h-[160px] lg:max-h-[200px] aspect-[2.2] flex items-end justify-start px-6 py-3 t-big-light transition-all cursor-pointer text-black overflow-clip'
    const border = 'rounded-[26px]'
    const bg = [
        '      bg-[radial-gradient(ellipse_130%_120%_at_0%_100%,hsl(53deg_97%_45%)_0%,hsl(53deg_97%_55%)_60%,#F49629_90%,#F17E22_100%)]',
        'hover:bg-[radial-gradient(ellipse_130%_120%_at_-20%_120%,#FCE640_0%,#FCE640_60%,#F49629_90%,#F17E22_100%)]',
    ]

    return (
        <button
            className={cn(style, border, bg, className)}
            onMouseDown={carmackClick(() => history.push('/host'))}
            onKeyDown={(ev) => ev.key === ' ' && history.push('/host')}
        >
            <div className="flex flex-col items-start">
                <Trans i18nKey="<1>Host</1> <2>Session</2>" t={t}>
                    <div className="font-normal">Host</div>
                    <div className="font-light">Session</div>
                </Trans>
                <IconCircle light className="absolute w-[50px] h-[50px] top-[13px] right-[13px]">
                    <RadioCircleIcon className="text-muse-fg" style={{ fontSize: '1.4rem' }} />
                </IconCircle>
            </div>
        </button>
    )
}

type DashSecondaryButtonProps = {
    className?: string
    children?: React.ReactNode
    onClick: () => void
}
function DashSecondaryButton({ className, children, onClick }: DashSecondaryButtonProps) {
    const style =
        'relative group w-full max-h-[160px] lg:max-h-[200px] aspect-[1.09] flex items-end justify-start px-6 py-3 t-lg overflow-clip cursor-pointer'
    const border = 'border-muse-toplit-low rounded-[26px]'
    const bg = 'bg-white/10 hover:bg-white/7 shadow-glow'
    return (
        <button
            className={cn(style, border, bg, className)}
            onMouseDown={carmackClick(() => onClick())}
            onKeyDown={(ev) => ev.key === ' ' && onClick()}
        >
            <div className="flex flex-col items-start">
                <div className="absolute top-0 left-0 w-[207px] h-[148px] bg-[url('/images/sparkle-dash-button.webp')]"></div>
                {children}
            </div>
        </button>
    )
}

type SessionTabsProps = {
    frontpage: ApiFrontPageResponse | null
    scheduledSessionsFilter: ReturnType<typeof useScheduledSessionsFilter>
    loading: boolean
}
function SessionTabs({ frontpage, loading, scheduledSessionsFilter }: SessionTabsProps) {
    const { t } = useTranslation()
    // Rember the currrent tab index between page reloads
    const initialTab = Number(window.localStorage.getItem('dashboard-tab-index') ?? '0')

    const containerRef = useRef<HTMLDivElement>(null)

    return (
        <div className="hidden md:block min-w-[350px]">
            <div className="fixed -right-1 top-[123px] lg:top-" ref={containerRef}>
                <DiagonalTabs
                    initialIndex={initialTab}
                    widthFitTabs
                    onTabChange={(index) => {
                        window.localStorage.setItem('dashboard-tab-index', index.toString())
                    }}
                    containerWidth={350}
                    containerHeight={715}
                    responsive
                    childrenByTabIndex={[
                        <ScheduledSessions scheduledSessionsFilter={scheduledSessionsFilter} loading={loading} />,
                        <DashRooms rooms={frontpage?.recentRooms ?? []} loading={loading} />,
                        <DashRooms rooms={frontpage?.permanentRooms ?? []} loading={loading} permanent />,
                    ]}
                    tabs={[
                        { id: 'schedule', label: t('Schedule') },
                        { id: 'history', label: t('History'), badge: frontpage?.recentRooms?.length ?? 0 },
                        { id: 'room', label: t('Room'), badge: frontpage?.permanentRooms?.length ?? 0 },
                    ]}
                />
            </div>
        </div>
    )
}

type DashRoomsProps = {
    rooms: DashboardRoom[]
    loading: boolean
    permanent?: boolean
}
function DashRooms({ rooms, loading, permanent }: DashRoomsProps) {
    const { t } = useTranslation()

    const loadingEl = loading && (
        <div className="w-full h-full flex items-center justify-center">
            <Loader className="bg-neutral-700" width="40%" />
        </div>
    )
    const roomEls = rooms.map((room) => (
        <DashRoomCard key={room.joinCode} room={room} permanent={permanent} className="px-[16px]" />
    ))

    const emptyText = permanent ? t('No Permanent Rooms') : t('No Recent Sessions')
    const emptySubtext = permanent ? t('Host a new session and select permanent') : undefined

    return (
        <div
            className={cn(
                'h-[calc(100vh-123px-30px)] overflow-y-hidden pt-[25px] pr-4',
                rooms.length > 0 && !loading ? 'px-[8px]' : ''
            )}
        >
            <div className="h-full flex flex-col gap-[8px] pb-[10px] px-[8px] overflow-y-auto overflow-x-hidden">
                {loadingEl}
                {rooms.length === 0 && !loading && (
                    <EmptyTab
                        className="mt-[38px]"
                        icon={<RadioCircleIcon />}
                        text={emptyText}
                        subtext={emptySubtext}
                    />
                )}
                {!loadingEl && roomEls}
            </div>
        </div>
    )
}

function useRestoreWindowBounds() {
    useNonIdempotentEffect(() => {
        if (!env.IS_ELECTRON) {
            return
        }

        const storedWindowMetrics = persistent.getObject('StartupPage.winmetrics')
        if (storedWindowMetrics) {
            log.isVerbose() &&
                log.verbose(
                    'setWindowBounds',
                    `call ElectronIPC.setWindowBounds() with ` +
                        `storedWindowMetrics=${JSON.stringify(storedWindowMetrics)} (first time).`
                )
            ElectronIPC.setWindowBounds(storedWindowMetrics)
            setTimeout(() => {
                log.isVerbose() &&
                    log.verbose(
                        'setWindowBounds',
                        `call ElectronIPC.setWindowBounds() with ` +
                            `storedWindowMetrics=${JSON.stringify(storedWindowMetrics)} (second time).`
                    )
                ElectronIPC.setWindowBounds(storedWindowMetrics)
            }, 100)
        } else {
            const d = Constants.choose_initial_mode_page_window_dim
            const defaultWindowMetrics = {
                width: d.width,
                height: d.height,
                center: true,
            }
            log.isVerbose() &&
                log.verbose(
                    'setWindowBounds',
                    `storedWindowMetrics is null; set default window size ` +
                        `defaultWindowMetrics=${JSON.stringify(defaultWindowMetrics)}`
                )
            ElectronIPC.setWindowBounds(defaultWindowMetrics)
        }

        ElectronIPC.setPowerSaveBlocker(false)
    }, [])
}

function useBusyAppKillBlocker() {
    const [appState, appDispatch] = useAppContext()

    useEffect(() => {
        // block window from closing if screen recording conversion in progress.
        const onWindowClosing = () => {
            if (appState.appState.screenRecordingConversionProgress) {
                appDispatch({
                    type: 'update_app_state',
                    value: { isAppExitConfirmationDialogOpen: true },
                })
            } else {
                ElectronIPC.quitApp({ immediate: true })
            }
        }

        ElectronIPC.lockMainWindowClosing(true, onWindowClosing)

        return () => {
            ElectronIPC.lockMainWindowClosing(false, onWindowClosing)
        }
    }, [appDispatch, appState])
}

function useExternalEventHandler() {
    const history = useHistory()

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            console.log(`ChooseInitialModePage detected external event: %O`, externalEvent)

            if (externalEvent.eventName === 'join-room-link') {
                const { error, code, metadata } = parseJoinRoomLink(externalEvent.value)
                if (code) {
                    history.push(`/join/${code}`)
                }
            } else if (externalEvent.eventName === 'join-room-code') {
                if (externalEvent.value) {
                    history.push(`/join/${externalEvent.value}`)
                }
            } else if (externalEvent.eventName === 'menu-click') {
                const opcode = externalEvent.value?.opcode
                if (opcode === 'muse_about') {
                } else if (opcode === 'muse_accountinfo') {
                } else if (opcode === 'muse_signout') {
                    // TODO: set_showLogout(true)
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])
}
